// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.js';
import type { ResponseDetail, ResponseListResponse, ResponseSummary } from './types.js';

export interface ResponseListFilters {
  questionnaireId?: string;
  status?: string;
  flagged?: boolean;
  archived?: boolean;
}

export const responsesQueryKey = (filters: ResponseListFilters) => ['responses', filters] as const;
export const responseQueryKey = (id: string) => ['responses', id] as const;

function toQuery(filters: ResponseListFilters): string {
  const params = new URLSearchParams();
  if (filters.questionnaireId) params.set('questionnaireId', filters.questionnaireId);
  if (filters.status) params.set('status', filters.status);
  if (filters.flagged !== undefined) params.set('flagged', String(filters.flagged));
  if (filters.archived !== undefined) params.set('archived', String(filters.archived));
  const s = params.toString();
  return s.length === 0 ? '' : `?${s}`;
}

export function useResponses(filters: ResponseListFilters = {}) {
  return useQuery<ResponseSummary[]>({
    queryKey: responsesQueryKey(filters),
    queryFn: async () => {
      const res = await apiFetch<ResponseListResponse>(`/api/responses${toQuery(filters)}`);
      return res.items;
    },
  });
}

export function useResponse(id: string | undefined) {
  return useQuery<ResponseDetail>({
    queryKey: id !== undefined ? responseQueryKey(id) : ['responses', 'noop'],
    enabled: id !== undefined,
    queryFn: () => apiFetch<ResponseDetail>(`/api/responses/${id}`),
  });
}

export interface UpdateResponseStatusInput {
  id: string;
  status: string;
}

export function useUpdateResponseStatus() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; status: string; updatedAt: string },
    Error,
    UpdateResponseStatusInput
  >({
    mutationFn: ({ id, status }) =>
      apiFetch(`/api/responses/${id}`, {
        method: 'PATCH',
        body: { status },
      }),
    onSuccess: async (_d, vars) => {
      await qc.invalidateQueries({ queryKey: ['responses'] });
      await qc.invalidateQueries({ queryKey: responseAuditQueryKey(vars.id) });
    },
  });
}

export interface UpdateResponseFlagInput {
  id: string;
  flagged: boolean;
}

export function useUpdateResponseFlag() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; status: string; flagged: boolean; updatedAt: string },
    Error,
    UpdateResponseFlagInput
  >({
    mutationFn: ({ id, flagged }) =>
      apiFetch(`/api/responses/${id}`, {
        method: 'PATCH',
        body: { flagged },
      }),
    onSuccess: async (_d, vars) => {
      await qc.invalidateQueries({ queryKey: ['responses'] });
      await qc.invalidateQueries({ queryKey: responseAuditQueryKey(vars.id) });
    },
  });
}

export interface UpdateResponseArchiveInput {
  id: string;
  archived: boolean;
}

export function useUpdateResponseArchive() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; status: string; flagged: boolean; updatedAt: string },
    Error,
    UpdateResponseArchiveInput
  >({
    mutationFn: ({ id, archived }) =>
      apiFetch(`/api/responses/${id}`, {
        method: 'PATCH',
        body: { archived },
      }),
    onSuccess: async (_d, vars) => {
      await qc.invalidateQueries({ queryKey: ['responses'] });
      await qc.invalidateQueries({ queryKey: responseAuditQueryKey(vars.id) });
    },
  });
}

/* -------------------------------------------------------------------- */
/*  Per-response audit timeline + notes (5xe / bib)                     */
/* -------------------------------------------------------------------- */

export interface AuditTimelineActor {
  id: string | null;
  kind: 'user' | 'magic_link' | 'system';
  displayName: string | null;
  role: string | null;
}

export interface AuditTimelineEntry {
  id: string;
  actor: AuditTimelineActor;
  action: string;
  metadata: Record<string, unknown>;
  occurredAt: string;
}

export const responseAuditQueryKey = (id: string) => ['responses', id, 'audit'] as const;
export const responseNotesQueryKey = (id: string) => ['responses', id, 'notes'] as const;

export function useResponseAudit(id: string | undefined) {
  return useQuery<AuditTimelineEntry[]>({
    queryKey: id !== undefined ? responseAuditQueryKey(id) : ['responses', 'audit', 'noop'],
    enabled: id !== undefined,
    queryFn: async () => {
      const res = await apiFetch<{ items: AuditTimelineEntry[] }>(`/api/responses/${id}/audit`);
      return res.items;
    },
  });
}

export interface ResponseNote {
  id: string;
  responseId: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  author: {
    id: string;
    displayName: string;
    role: string;
  };
}

export function useResponseNotes(id: string | undefined) {
  return useQuery<ResponseNote[]>({
    queryKey: id !== undefined ? responseNotesQueryKey(id) : ['responses', 'notes', 'noop'],
    enabled: id !== undefined,
    queryFn: async () => {
      const res = await apiFetch<{ items: ResponseNote[] }>(`/api/responses/${id}/notes`);
      return res.items;
    },
  });
}

export function useCreateNote(responseId: string) {
  const qc = useQueryClient();
  return useMutation<{ id: string; createdAt: string }, Error, string>({
    mutationFn: (body) =>
      apiFetch(`/api/responses/${responseId}/notes`, { method: 'POST', body: { body } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: responseNotesQueryKey(responseId) });
      await qc.invalidateQueries({ queryKey: responseAuditQueryKey(responseId) });
    },
  });
}

export function useUpdateNote(responseId: string) {
  const qc = useQueryClient();
  return useMutation<{ id: string; updatedAt: string }, Error, { noteId: string; body: string }>({
    mutationFn: ({ noteId, body }) =>
      apiFetch(`/api/responses/${responseId}/notes/${noteId}`, {
        method: 'PATCH',
        body: { body },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: responseNotesQueryKey(responseId) });
      await qc.invalidateQueries({ queryKey: responseAuditQueryKey(responseId) });
    },
  });
}

export function useDeleteNote(responseId: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (noteId) =>
      apiFetch(`/api/responses/${responseId}/notes/${noteId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: responseNotesQueryKey(responseId) });
      await qc.invalidateQueries({ queryKey: responseAuditQueryKey(responseId) });
    },
  });
}
